import { useState, useEffect } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table'
import { Link } from 'react-router-dom'
import { Eye, Pencil, CheckCircle, XCircle } from 'lucide-react'
import type {
  BenefitRule,
  BenefitRuleFilters,
  TriggerType,
  ActionType,
} from '../types'
import {
  TRIGGER_TYPE_LABELS,
  ACTION_TYPE_LABELS,
} from '../types'

const columnHelper = createColumnHelper<BenefitRule>()

const columns = [
  columnHelper.accessor('id', { header: 'ID', size: 60 }),
  columnHelper.accessor('name', { header: 'Nombre' }),
  columnHelper.accessor('trigger_type', {
    header: 'Gatillo',
    cell: ({ getValue }) => (
      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
        {TRIGGER_TYPE_LABELS[getValue()]}
      </span>
    ),
  }),
  columnHelper.accessor('action_type', {
    header: 'Acción',
    cell: ({ getValue }) => (
      <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">
        {ACTION_TYPE_LABELS[getValue()]}
      </span>
    ),
  }),
  columnHelper.accessor('is_active', {
    header: 'Estado',
    size: 80,
    cell: ({ getValue }) =>
      getValue() ? (
        <span className="flex items-center gap-1 text-green-600">
          <CheckCircle size={16} /> Activa
        </span>
      ) : (
        <span className="flex items-center gap-1 text-gray-400">
          <XCircle size={16} /> Inactiva
        </span>
      ),
  }),
  columnHelper.accessor('priority', { header: 'Prioridad', size: 80 }),
  columnHelper.display({
    id: 'actions',
    header: 'Acciones',
    size: 120,
    cell: ({ row }) => (
      <div className="flex gap-2">
        <Link
          to={`/benefits/${row.original.id}`}
          className="text-primary-600 hover:text-primary-800 transition-colors"
          title="Ver detalle"
        >
          <Eye size={18} />
        </Link>
        <Link
          to={`/benefits/${row.original.id}/edit`}
          className="text-primary-600 hover:text-primary-800 transition-colors"
          title="Editar"
        >
          <Pencil size={18} />
        </Link>
      </div>
    ),
  }),
]

interface LocalFilters {
  is_active: string
  trigger_type: string
  action_type: string
}

function filtersToLocal(filters: BenefitRuleFilters): LocalFilters {
  return {
    is_active: filters.is_active === undefined ? '' : String(filters.is_active),
    trigger_type: filters.trigger_type ?? '',
    action_type: filters.action_type ?? '',
  }
}

interface BenefitRulesTableProps {
  data: BenefitRule[]
  total: number
  page: number
  limit: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  isLoading: boolean
  filters: BenefitRuleFilters
  onApplyFilters: (filters: Partial<BenefitRuleFilters>) => void
  onClearFilters: () => void
}

export function BenefitRulesTable({
  data,
  total,
  page,
  limit,
  onPageChange,
  onLimitChange,
  isLoading,
  filters,
  onApplyFilters,
  onClearFilters,
}: BenefitRulesTableProps) {
  const [localFilters, setLocalFilters] = useState<LocalFilters>(() => filtersToLocal(filters))

  // Sync local state when applied filters change externally (e.g. clearFilters)
  useEffect(() => {
    setLocalFilters(filtersToLocal(filters))
  }, [filters])

  const hasFilters = filters.is_active !== undefined || filters.trigger_type !== undefined || filters.action_type !== undefined

  const handleApply = () => {
    onApplyFilters({
      is_active: localFilters.is_active === '' ? undefined : localFilters.is_active === 'true',
      trigger_type: (localFilters.trigger_type || undefined) as TriggerType | undefined,
      action_type: (localFilters.action_type || undefined) as ActionType | undefined,
    })
  }

  const handleClear = () => {
    onClearFilters()
  }

  const pagination: PaginationState = {
    pageIndex: page - 1,
    pageSize: limit,
  }

  const pageCount = Math.ceil(total / limit)

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: { pagination },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater
      if (newPagination.pageSize !== limit) {
        onLimitChange(newPagination.pageSize)
      }
      if (newPagination.pageIndex + 1 !== page) {
        onPageChange(newPagination.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })

  if (isLoading) {
    return <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }

  return (
    <div>
      {/* Filter bar */}
      <div className="p-4 border-b flex flex-wrap items-center gap-3">
        <select
          value={localFilters.is_active}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, is_active: e.target.value }))}
          className="input w-auto text-sm"
        >
          <option value="">Estado: Todos</option>
          <option value="true">Activas</option>
          <option value="false">Inactivas</option>
        </select>

        <select
          value={localFilters.trigger_type}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, trigger_type: e.target.value }))}
          className="input w-auto text-sm"
        >
          <option value="">Gatillo: Todos</option>
          {Object.entries(TRIGGER_TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>

        <select
          value={localFilters.action_type}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, action_type: e.target.value }))}
          className="input w-auto text-sm"
        >
          <option value="">Acción: Todas</option>
          {Object.entries(ACTION_TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>

        <button onClick={handleApply} className="btn btn-primary text-sm">
          Aplicar Filtros
        </button>

        {hasFilters && (
          <button onClick={handleClear} className="btn btn-secondary text-sm">
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-50 border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-4 py-3 font-medium text-gray-700">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-gray-500">
                  No se encontraron reglas de beneficio
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50 transition-colors">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {total} resultado{total !== 1 ? 's' : ''} - Página {page} de {pageCount || 1}
          </span>
          <select
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="input w-auto text-sm"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} por página
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  )
}
